import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface BancoModel {
    humanName: string
    hasOperation: boolean
}

export interface DadosBancariosSubcontaApi {
    id: number | null
    dataPersonType: number | null
    nomeEmpresa: string
    telefone: string
    endereco: string
    estado: string
    cidade: string
    cep: string
    responsavelName: string
    responsavelCpf: string
    cpfCnpj: string
    nomeBanco: string
    agencia: string
    numeroConta: string
    tipoConta: string
    operacao: string
    possuiTokensCriadosIugu: boolean
    possuiDadosBancariosCompletos: boolean
    nomeEstabelecimento: string
    bancos: BancoModel[]
}

export interface SalvarDadosBancariosPayload {
    dataPersonType: number | null
    nomeEmpresa: string
    telefone: string
    endereco: string
    estado: string
    cidade: string
    cep: string
    responsavelName: string
    responsavelCpf: string
    cpfCnpj: string
    nomeBanco: string
    agencia: string
    numeroConta: string
    tipoConta: string
    operacao: string
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterDadosBancariosSubconta = async function (
    authorization: string
): Promise<DadosBancariosSubcontaApi | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<DadosBancariosSubcontaApi>>(
                "dadosBancarios/ObterDadosBancariosSubconta",
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<DadosBancariosSubcontaApi>(
            response,
            "ObterDadosBancariosSubconta"
        )
    } catch (error) {
        RegistrarLogErro(error, "ObterDadosBancariosSubconta", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const criarSubconta = async function (
    authorization: string,
    nomeEstabelecimento: string
): Promise<{ id: number; possuiTokensCriadosIugu: boolean } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ id: number; possuiTokensCriadosIugu: boolean }>>(
                "dadosBancarios/CriarSubconta",
                { nomeEstabelecimento },
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ id: number; possuiTokensCriadosIugu: boolean }>(
            response,
            "CriarSubconta"
        )
    } catch (error) {
        RegistrarLogErro(error, "CriarSubconta", nomeEstabelecimento)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarDadosBancarios = async function (
    authorization: string,
    payload: SalvarDadosBancariosPayload
): Promise<{ id: number; possuiDadosBancariosCompletos: boolean } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ id: number; possuiDadosBancariosCompletos: boolean }>>(
                "dadosBancarios/SalvarDadosBancarios",
                payload,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ id: number; possuiDadosBancariosCompletos: boolean }>(
            response,
            "SalvarDadosBancarios"
        )
    } catch (error) {
        RegistrarLogErro(error, "SalvarDadosBancarios", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarCredenciaisIugu = async function (
    authorization: string,
    tokenIuguId: string,
    tokenIuguCabecalho: string
): Promise<{ possuiTokensCriadosIugu: boolean } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ possuiTokensCriadosIugu: boolean }>>(
                "dadosBancarios/SalvarCredenciaisIugu",
                { tokenIuguId, tokenIuguCabecalho },
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ possuiTokensCriadosIugu: boolean }>(
            response,
            "SalvarCredenciaisIugu"
        )
    } catch (error) {
        RegistrarLogErro(error, "SalvarCredenciaisIugu", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirDadosBancarios = async function (
    authorization: string
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<{ codErro?: number; mensagem?: string } | null>(
                "dadosBancarios/ExcluirDadosBancarios",
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return {
                    data: erroParaRespostaRaw(error) as { codErro?: number; mensagem?: string },
                }
            })

        const data = response.data
        if (data && typeof data === "object" && "codErro" in data && (data.codErro ?? 0) > 0) {
            return { retornoErro: data.codErro!, mensagem: data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirDadosBancarios", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
